import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {Config, ConfigService} from "../../config";
import {CountrySettingsDto} from "../../dto/country-settings-dto";
import {AppointmentInfo} from "../../../features/products/appointment/models/appointment-info";
import {Schedule} from "../../../features/products/appointment/models/time-slots";
import {
  CreateAppointmentDto,
  CreateAppointmentResponse
} from "../../../features/products/appointment/models/create-appointment-dto";
import {Utils} from "../../../shared/utils/generics.utils";
import {ProviderServices} from "../../../features/products/appointment/models/provider-services";
import {UiUtils} from "../../../shared/utils/ui.utils";
import {CompanyResponseDto} from "../../dto/company-response-dto";
import {catchError, throwError} from "rxjs";
import {
  GetVehicleAndAnalysisFromPictureRequestDto,
  GetVehicleAndAnalysisFromPictureResponseDto
} from "../../dto/get-vehicle-and-analysis-from-picture-dto";
import {GarageSearch, toHttpParams} from "../../models/garage-search";
import {NearestGarages} from "../../models/nearest-garages";

@Injectable({
  providedIn: 'root'
})
export class ExternalApiService {

  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private config = this.configService.getConfig().rool

  setConfig(config: Config) {
    this.config = config.rool;
  }

  getCompany(company: string, language: string) {
    const params = new HttpParams({fromObject: {language}});
    return this.http.get<CompanyResponseDto>(`${this.config?.externalApi.url}/v1/web-app/companies/${company}`, {params})
      .pipe(
        catchError((err) => {
          if (err.status === 404 && company.includes('-')) {
            const fallbackCompany = company.split('-')[0];
            return this.http.get<CompanyResponseDto>(`${this.config?.externalApi.url}/v1/web-app/companies/${fallbackCompany}`, {params})
          }
          return throwError(() => err);
        })
      );
  }

  getCountrySettings(countryCode: string) {
    return this.http.get<CountrySettingsDto>(`${this.config.externalApi.url}/v1/countries/${countryCode}/settings`);
  }

  getAppointmentInfo(context: string) {
    return this.http.get<AppointmentInfo>(`${this.config.externalApi.url}/v1/appointments`, {
      params: new HttpParams({fromObject: {context}})
    });
  }

  getNearestGarages(dto: GarageSearch) {
    return this.http.get<NearestGarages>(`${this.config.externalApi.url}/v1/garages/nearest`, {
      params: new HttpParams({fromObject: toHttpParams(dto)})
    });
  }

  createAppointment(dto: CreateAppointmentDto) {
    return this.http.post<CreateAppointmentResponse>(`${this.config.externalApi.url}/v1/appointments`, dto);
  }

  getTimeSlots(garageId: number, serviceId: number, week: number) {
    return this.http.get<Schedule>(`${this.config.externalApi.url}/v1/garages/${garageId}/services/${serviceId}/timeslots`, {
      params: new HttpParams({fromObject: {week}})
    });
  }

  getProviderServices(context: string, provider: string = Utils.getCompanyFromDomain()) {
    return this.http.get<ProviderServices>(`${this.config.externalApi.url}/v1/providers/euromaster/services`, {
      params: new HttpParams({fromObject: {context, language: UiUtils.getBrowserLanguage()}})
    });
    // return this.http.get<ProviderServices>(`${this.config.externalApi.url}/v1/providers/${provider}/services`, {
    //   params: new HttpParams({fromObject: { context }})
    // });
  }

  createAnalysisFromLicensePlatePicture(
    request: GetVehicleAndAnalysisFromPictureRequestDto,
    picture: File
  ) {
    const formData = new FormData();

    formData.append('licensePlatePicture', picture);
    formData.append(
      'body',
      new Blob([JSON.stringify(request)], { type: 'application/json' })
    );

    return this.http.post<GetVehicleAndAnalysisFromPictureResponseDto>(
      `${this.config.externalApi.url}/v1/web-app/analyses/by-picture`,
      formData
    );
  }
}
